<template>
  <div>
    <!-- In Impostazioni compare solo il riquadro di lancio: l'editor vero vive in un dialog a tutto schermo -->
    <v-card outlined>
      <v-card-title class="d-flex align-center">
        <v-icon left class="mr-2">mdi-state-machine</v-icon>
        {{ t('admConfig.statusEditor.title') }}
        <v-chip v-if="hasChanges" color="warning" size="small" label class="ml-3">
          {{ t('admConfig.statusEditor.unsaved') }}
        </v-chip>
      </v-card-title>
      <v-card-text>
        <div class="text-body-2 text-medium-emphasis mb-4">
          {{ t('admConfig.statusEditor.description') }}
        </div>
        <div class="d-flex align-center flex-wrap" style="gap: 12px;">
          <v-btn color="primary" prepend-icon="mdi-sitemap" @click="openEditor">
            {{ t('admConfig.statusEditor.openEditor') }}
          </v-btn>
          <span class="text-caption text-medium-emphasis">
            {{ t('admConfig.statusEditor.statusCount', { count: totalStatusCount, types: workingTypes.length }) }}
          </span>
        </div>
      </v-card-text>
    </v-card>

    <!-- Editor a tutto schermo -->
    <v-dialog
      v-model="editorDialog"
      fullscreen
      :scrim="false"
      transition="dialog-bottom-transition"
    >
      <v-card class="d-flex flex-column">
        <v-toolbar density="compact" color="primary" dark class="flex-shrink-0">
          <v-btn icon @click="requestClose">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ t('admConfig.statusEditor.title') }}</v-toolbar-title>
          <v-chip v-if="hasChanges" color="warning" size="small" label class="ml-3">
            {{ t('admConfig.statusEditor.unsaved') }}
          </v-chip>
          <v-spacer />
          <v-btn
            variant="text"
            prepend-icon="mdi-undo"
            :disabled="!hasChanges || saving"
            @click="resetChanges"
          >
            {{ t('admConfig.statusEditor.discard') }}
          </v-btn>
          <v-btn
            variant="flat"
            color="white"
            class="text-primary ml-2"
            prepend-icon="mdi-content-save"
            :loading="saving"
            :disabled="!hasChanges || blockingErrors.length > 0"
            @click="save"
          >
            {{ t('admConfig.statusEditor.save') }}
          </v-btn>
        </v-toolbar>

        <v-card-text class="overflow-y-auto flex-grow-1">
          <!-- Riferimenti a stati inesistenti: bloccano il salvataggio, lascerebbero processi senza via d'uscita -->
          <v-alert v-if="blockingErrors.length" type="error" variant="tonal" density="comfortable" class="mb-3">
            <div class="font-weight-medium">{{ t('admConfig.statusEditor.blockedTitle') }}</div>
            <ul class="mt-2 ml-4">
              <li v-for="(e, i) in blockingErrors" :key="i" class="text-body-2">{{ e }}</li>
            </ul>
          </v-alert>

          <v-alert v-if="saveError" type="error" variant="tonal" density="comfortable" class="mb-3">
            <div>{{ saveError }}</div>
            <ul v-if="saveErrorDetails.length" class="mt-2 ml-4">
              <li v-for="(e, i) in saveErrorDetails" :key="i" class="text-body-2">{{ e }}</li>
            </ul>
          </v-alert>

          <v-alert v-if="saveSuccess" type="success" variant="tonal" density="compact" class="mb-3">
            {{ saveSuccess }}
          </v-alert>

          <!-- Un tab per tipo di processo -->
          <v-tabs v-model="activeTypeId" density="compact" color="primary" class="mb-3">
            <v-tab v-for="type in workingTypes" :key="type.id" :value="type.id">
              {{ type.descr }}
              <v-chip size="x-small" label class="ml-2">{{ (type.statusList || []).length }}</v-chip>
            </v-tab>
          </v-tabs>

          <template v-if="activeType">
            <!-- Intestazione del tipo: nome, ordine dei gruppi di campi, sezione delle istruzioni -->
            <div class="d-flex align-center flex-wrap mb-3" style="gap: 12px;">
              <v-text-field
                v-model="activeType.descr"
                :label="t('admConfig.statusEditor.typeDescr')"
                density="compact"
                hide-details
                counter="60"
                style="max-width: 260px;"
              />
              <v-chip size="small" label variant="tonal">
                {{ t('admConfig.statusEditor.typeId', { id: activeType.id }) }}
              </v-chip>
              <v-chip size="small" label variant="tonal" color="deep-purple">
                {{ t('admConfig.statusEditor.instructionSection', { section: activeSection }) }}
              </v-chip>
              <v-text-field
                v-if="!hasResolvedSection"
                v-model="sectionOverrides[activeType.id]"
                :label="t('admConfig.statusEditor.sectionOverride')"
                :hint="t('admConfig.statusEditor.sectionOverrideHint')"
                density="compact"
                persistent-hint
                style="max-width: 220px;"
              />
            </div>

            <v-alert
              v-if="!instructionSectionExists"
              type="info"
              variant="tonal"
              density="compact"
              class="mb-3"
            >
              {{ t('admConfig.statusEditor.sectionMissing', { section: activeSection }) }}
            </v-alert>

            <!-- Ordine dei gruppi di campi nel form: trascinare per riordinare -->
            <div class="mb-4">
              <div class="text-caption text-medium-emphasis mb-1">
                {{ t('admConfig.statusEditor.groupOrder') }}
              </div>
              <div class="d-flex align-center flex-wrap" style="gap: 6px;">
                <v-chip
                  v-for="(group, gi) in (activeType.groupOrder || [])"
                  :key="`${group}-${gi}`"
                  size="small"
                  label
                  closable
                  draggable="true"
                  :class="{ 'group-chip--dragging': groupDragIndex === gi }"
                  @dragstart="groupDragIndex = gi"
                  @dragend="groupDragIndex = null"
                  @dragover.prevent
                  @drop.prevent="onGroupDrop(gi)"
                  @click:close="removeGroup(gi)"
                >
                  <v-icon start size="x-small">mdi-drag</v-icon>
                  {{ group }}
                </v-chip>
                <v-menu :close-on-content-click="false">
                  <template v-slot:activator="{ props }">
                    <v-btn size="x-small" variant="text" prepend-icon="mdi-plus" v-bind="props">
                      {{ t('admConfig.statusEditor.addGroup') }}
                    </v-btn>
                  </template>
                  <v-card min-width="300">
                    <v-card-text>
                      <v-combobox
                        v-model="newGroupName"
                        :items="knownGroupNames"
                        :label="t('admConfig.statusEditor.groupName')"
                        density="compact"
                        hide-details
                      />
                    </v-card-text>
                    <v-card-actions>
                      <v-spacer />
                      <v-btn size="small" variant="flat" color="primary" @click="addGroup">
                        {{ t('admConfig.statusEditor.addGroup') }}
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-menu>
              </div>
            </div>

            <div class="d-flex align-center flex-wrap mb-2" style="gap: 12px;">
              <v-btn size="small" color="primary" variant="tonal" prepend-icon="mdi-plus" @click="addStatus">
                {{ t('admConfig.statusEditor.addStatus') }}
              </v-btn>
              <span class="text-caption text-medium-emphasis">
                {{ t('admConfig.statusEditor.dragHint') }}
              </span>
            </div>

            <!-- Stati nell'ordine in cui appaiono in Dashboard e nel selettore di stato iniziale -->
            <div
              v-for="(status, index) in activeType.statusList"
              :key="`${status.id}-${index}`"
              class="status-row d-flex align-center pa-2 rounded"
              :class="{ 'status-row--dragging': dragIndex === index }"
              draggable="true"
              @dragstart="dragIndex = index"
              @dragend="dragIndex = null"
              @dragover.prevent
              @drop.prevent="onDrop(index)"
            >
              <v-icon size="small" color="grey" class="mr-2 drag-handle">mdi-drag</v-icon>

              <span class="status-swatch mr-2" :style="{ background: status.color || '#BDBDBD' }"></span>
              <span class="status-icon mr-2">{{ status.icon || '•' }}</span>

              <div class="flex-grow-1" style="min-width: 0;">
                <div class="d-flex align-center flex-wrap" style="gap: 6px;">
                  <span class="text-body-2 font-weight-medium">{{ status.descr }}</span>
                  <code class="text-caption text-medium-emphasis">id {{ status.id }}</code>
                  <v-chip v-if="countsFor(status.id).active" size="x-small" label variant="tonal" color="blue-grey">
                    {{ t('admConfig.statusEditor.inUse', { count: countsFor(status.id).active }) }}
                  </v-chip>
                </div>
                <div class="d-flex align-center flex-wrap mt-1" style="gap: 4px;">
                  <v-chip v-if="status.group" size="x-small" label variant="tonal">
                    {{ t(`dashboard.groups.${status.group}`, status.group) }}
                  </v-chip>
                  <!-- Profili che operano la fase. Nessun chip = fase di tutti. -->
                  <v-chip
                    v-for="roleId in (status.roles || [])"
                    :key="`role-${roleId}`"
                    size="x-small"
                    label
                    variant="tonal"
                    color="deep-purple"
                    prepend-icon="mdi-account-lock-outline"
                  >
                    {{ roleLabelOf(roleId) }}
                  </v-chip>
                  <v-chip v-if="status.followUpDays != null" size="x-small" label variant="tonal" color="teal">
                    {{ t('admConfig.statusEditor.followUpChip', { days: status.followUpDays }) }}
                  </v-chip>
                  <v-chip size="x-small" label variant="tonal" color="indigo">
                    {{ nextStatesLabel(status) }}
                  </v-chip>
                  <v-chip v-if="status.isOptional" size="x-small" label variant="tonal">
                    {{ t('admConfig.statusEditor.status.isOptional') }}
                  </v-chip>
                  <v-chip v-if="status.canArchive" size="x-small" label variant="tonal">
                    {{ t('admConfig.statusEditor.status.canArchive') }}
                  </v-chip>
                  <v-chip v-if="status.exclusive" size="x-small" label variant="tonal">
                    {{ t('admConfig.statusEditor.status.exclusive') }}
                  </v-chip>
                  <v-chip v-if="status.conditionalOn" size="x-small" label variant="tonal" color="purple">
                    {{ describeConditional(status.conditionalOn) }}
                  </v-chip>
                  <v-chip v-if="activeVerificationCount(status)" size="x-small" label variant="tonal" color="cyan">
                    {{ t('admConfig.statusEditor.verificationsChip', { count: activeVerificationCount(status) }) }}
                  </v-chip>
                  <v-chip v-if="disabledVerificationCount(status)" size="x-small" label variant="tonal" color="grey">
                    {{ t('admConfig.statusEditor.verificationsDisabledChip', { count: disabledVerificationCount(status) }) }}
                  </v-chip>
                  <v-chip v-if="status.deadlines?.length" size="x-small" label variant="tonal" color="orange">
                    {{ t('admConfig.statusEditor.deadlinesChip', { count: status.deadlines.length }) }}
                  </v-chip>
                  <v-chip
                    size="x-small"
                    label
                    variant="tonal"
                    :color="hasInstructions(status) ? 'green' : 'grey'"
                  >
                    {{ hasInstructions(status)
                      ? t('admConfig.statusEditor.instructionsPresent')
                      : t('admConfig.statusEditor.instructionsAbsent') }}
                  </v-chip>
                </div>
                <div v-if="warningsFor(status).length" class="d-flex align-center flex-wrap mt-1" style="gap: 4px;">
                  <v-chip
                    v-for="(w, wi) in warningsFor(status)"
                    :key="wi"
                    size="x-small"
                    label
                    variant="tonal"
                    :color="w.blocking ? 'error' : 'warning'"
                  >
                    {{ w.text }}
                  </v-chip>
                </div>
              </div>

              <v-btn icon variant="text" size="small" :title="t('admConfig.statusEditor.edit')" @click="editStatus(index)">
                <v-icon size="small">mdi-pencil</v-icon>
              </v-btn>

              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn icon variant="text" size="small" v-bind="props">
                    <v-icon size="small">mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item prepend-icon="mdi-pencil" @click="editStatus(index)">
                    <v-list-item-title>{{ t('admConfig.statusEditor.edit') }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item prepend-icon="mdi-content-copy" @click="startImpact('duplicate', status)">
                    <v-list-item-title>{{ t('admConfig.statusEditor.duplicate') }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item prepend-icon="mdi-identifier" @click="startImpact('changeId', status)">
                    <v-list-item-title>{{ t('admConfig.statusEditor.changeId') }}</v-list-item-title>
                  </v-list-item>
                  <v-divider />
                  <v-list-item prepend-icon="mdi-delete" base-color="error" @click="startImpact('delete', status)">
                    <v-list-item-title>{{ t('admConfig.statusEditor.delete') }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>

            <v-expansion-panels variant="accordion" class="mt-4">
              <v-expansion-panel>
                <v-expansion-panel-title class="text-body-2">
                  {{ t('admConfig.statusEditor.workflowPreview') }}
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <pre class="json-preview">{{ workflowPreview }}</pre>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <v-expansion-panel>
                <v-expansion-panel-title class="text-body-2">
                  {{ t('admConfig.statusEditor.jsonPreview') }}
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <pre class="json-preview">{{ jsonPreview }}</pre>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </template>
        </v-card-text>

        <!-- Dialog di modifica del singolo stato -->
        <StatusEditDialog
          v-model="editDialog"
          :status="editingStatus"
          :is-new="editingIndex === null"
          :section="activeSection"
          :all-statuses="activeType?.statusList || []"
          :ai-actions="aiActions"
          :fields="fieldsForActiveType"
          :roles="workflowRoles"
          :instructions-html="editingInstructionsHtml"
          @commit="onStatusCommit"
        />

        <!-- Analisi impatto di eliminazione, cambio id e duplicazione -->
        <StatusImpactDialog
          v-model="impactDialog"
          :operation="impactOperation"
          :status="impactStatus"
          :project-type="activeTypeId"
          :type-descr="activeType?.descr || ''"
          :working-types="workingTypes"
          :working-instructions="workingInstructions"
          :fields="fieldsForActiveType"
          :status-counts="statusCounts"
          @confirm="onImpactConfirm"
        />

        <!-- Conferma chiusura con modifiche pendenti -->
        <v-dialog v-model="confirmCloseDialog" max-width="480" persistent>
          <v-card>
            <v-card-title>{{ t('admConfig.statusEditor.confirmCloseTitle') }}</v-card-title>
            <v-card-text class="text-body-2">
              {{ t('admConfig.statusEditor.confirmCloseText') }}
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn variant="text" @click="confirmCloseDialog = false">
                {{ t('admConfig.statusEditor.keepEditing') }}
              </v-btn>
              <v-btn variant="text" color="error" @click="discardAndClose">
                {{ t('admConfig.statusEditor.discardAndClose') }}
              </v-btn>
              <v-btn
                variant="flat"
                color="primary"
                :loading="saving"
                :disabled="blockingErrors.length > 0"
                @click="saveAndClose"
              >
                {{ t('admConfig.statusEditor.saveAndClose') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useProjectStore } from '../stores/projects.js'
import StatusEditDialog from './StatusEditDialog.vue'
import StatusImpactDialog from './StatusImpactDialog.vue'

const { t } = useI18n()
const projectStore = useProjectStore()

// Ordine canonico delle proprietà: stesso whitelist del Backend. Serve a due cose: tenere il JSON
// leggibile e rendere stabile il confronto di hasChanges (altrimenti riaprire uno stato lo sporca).
const STATUS_PROPS = [
  'descr', 'id', 'color', 'icon', 'followUpDays', 'followUpRules', 'followUpCapField',
  'followUpCapReleaseField', 'group', 'isOptional', 'canArchive', 'exclusive',
  'conditionalOn', 'nextStates', 'instructionKey', 'verifications', 'deadlines'
]
const TYPE_PROPS = ['descr', 'id', 'groupOrder', 'statusList']

// Copia di lavoro: nulla tocca i file finché non si preme Salva
const workingTypes = ref([])
const workingInstructions = ref({})
const originalTypesJson = ref('')
const originalInstructionsJson = ref('')
const sectionOverrides = ref({})
const skills = ref([])
// Biblioteca delle azioni IA: qui è di sola lettura, si modifica dal suo editor in Impostazioni
const aiActions = computed(() => projectStore.aiActions || [])

// Biblioteca dei profili operativi: gli stati la richiamano per id, come per le azioni IA.
// Si edita in RolesEditor, qui si legge soltanto.
const workflowRoles = computed(() => projectStore.workflowRoles || [])
const roleLabelOf = (id) => workflowRoles.value.find(r => r.id === id)?.label || id
const statusCounts = ref([])
const activeTypeId = ref(0)
const dragIndex = ref(null)
const groupDragIndex = ref(null)
const newGroupName = ref('')
const saving = ref(false)
const saveError = ref('')
const saveErrorDetails = ref([])
const saveSuccess = ref('')
// Riscrittura opzionale della definizione campi decisa nell'analisi impatto, applicata al salvataggio
const pendingFieldsSave = ref(null)

const hasCfgChanges = computed(() => JSON.stringify(workingTypes.value) !== originalTypesJson.value)
const hasInstructionsChanges = computed(() => JSON.stringify(workingInstructions.value) !== originalInstructionsJson.value)
const hasChanges = computed(() => hasCfgChanges.value || hasInstructionsChanges.value || !!pendingFieldsSave.value)

const jsonPreview = computed(() => JSON.stringify({ projectTypes: workingTypes.value }, null, 2))
const totalStatusCount = computed(() =>
  workingTypes.value.reduce((sum, type) => sum + (type.statusList || []).length, 0)
)

const activeType = computed(() => workingTypes.value.find(type => type.id === activeTypeId.value) || null)
// I campi vivono in un file per tipo di processo: la mappa è già partizionata, niente filtro
const fieldsByType = computed(() => projectStore.fieldsByProjectType || {})
const fieldsForActiveType = computed(() => fieldsByType.value[String(activeTypeId.value)] || [])

// ---------- Caricamento ----------

// Riordina le proprietà note e scarta 'instructions', che getStatusInfo nello store inietta
// nell'oggetto stato per comodità del form: finirebbe scritta in cfg.json.
const canonical = (raw, order) => {
  const out = {}
  order.forEach(key => {
    if (raw[key] !== undefined && raw[key] !== null) out[key] = raw[key]
  })
  // Proprietà ignote conservate in coda: il Backend le segnalerà invece di perderle in silenzio
  Object.keys(raw).forEach(key => {
    if (!order.includes(key) && key !== 'instructions') out[key] = raw[key]
  })
  return out
}

const loadFromStore = () => {
  const raw = projectStore.configurations?.config?.projectTypes
  const cloned = Array.isArray(raw) ? JSON.parse(JSON.stringify(raw)) : []
  const types = cloned.map(type => {
    const canonicalType = canonical(type, TYPE_PROPS)
    canonicalType.statusList = (type.statusList || []).map(status => canonical(status, STATUS_PROPS))
    return canonicalType
  })
  workingTypes.value = types
  originalTypesJson.value = JSON.stringify(types)

  const rawInstructions = projectStore.instructions
  const instructions = rawInstructions && typeof rawInstructions === 'object'
    ? JSON.parse(JSON.stringify(rawInstructions))
    : {}
  workingInstructions.value = instructions
  originalInstructionsJson.value = JSON.stringify(instructions)

  pendingFieldsSave.value = null
}

const resetChanges = () => {
  loadFromStore()
  saveError.value = ''
  saveErrorDetails.value = []
  saveSuccess.value = ''
}

// ---------- Sezione delle istruzioni ----------

const slugify = (text) => (text || '')
  .normalize('NFD')
  .replace(/\p{M}/gu, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')

// La sezione non è scritta in cfg.json: vive dentro le stringhe instructionKey. Si deduce dal
// prefisso più usato nel tipo, così i tipi esistenti mantengono la loro sezione storica.
const resolvedSectionFor = (type) => {
  const counts = {}
  ;(type?.statusList || []).forEach(status => {
    const prefix = typeof status.instructionKey === 'string' ? status.instructionKey.split('.')[0] : null
    if (prefix) counts[prefix] = (counts[prefix] || 0) + 1
  })
  const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]
  return best ? best[0] : null
}

const sectionFor = (type) => {
  if (!type) return ''
  return resolvedSectionFor(type)
    || sectionOverrides.value[type.id]
    || slugify(type.descr) || `tipo${type.id}`
}

const activeSection = computed(() => sectionFor(activeType.value))
const hasResolvedSection = computed(() => !!resolvedSectionFor(activeType.value))
const instructionSectionExists = computed(() => !!workingInstructions.value[activeSection.value])

const instructionsHtmlFor = (status) => {
  const key = status?.instructionKey
  if (typeof key !== 'string') return ''
  const [section, statusKey] = key.split('.')
  return workingInstructions.value[section]?.[statusKey] || ''
}

const hasInstructions = (status) => !!instructionsHtmlFor(status)

// ---------- Riepilogo e avvisi di riga ----------

// Verifiche accese e spente: `enabled` assente significa accesa
const activeVerificationCount = (status) =>
  (status.verifications || []).filter(v => v.enabled !== false).length

const disabledVerificationCount = (status) =>
  (status.verifications || []).filter(v => v.enabled === false).length

const describeConditional = (cond) => {
  if (!cond || !cond.field) return ''
  if (Array.isArray(cond.values)) return `${cond.field} ∈ ${cond.values.join(', ')}`
  if (cond.notValue !== undefined) return `${cond.field} ≠ ${cond.notValue}`
  return `${cond.field} = ${cond.value}`
}

const nextStatesLabel = (status) => {
  const next = status.nextStates
  if (!Array.isArray(next) || !next.length) return t('admConfig.statusEditor.terminalState')
  return `→ ${next.join(', ')}`
}

const countsFor = (statusId) => {
  const row = statusCounts.value.find(
    r => r.projectType === activeTypeId.value && String(r.status) === String(statusId)
  )
  return { active: row?.active || 0, archived: row?.archived || 0 }
}

const skillExists = (skillId) => skills.value.some(s => s.id === skillId)

const warningsFor = (status) => {
  const warnings = []
  const type = activeType.value
  if (!type) return warnings

  const ids = new Set((type.statusList || []).map(s => Number(s.id)))
  const dangling = (status.nextStates || []).filter(n => !ids.has(Number(n)))
  if (dangling.length) {
    warnings.push({ blocking: true, text: t('admConfig.statusEditor.warnDangling', { ids: dangling.join(', ') }) })
  }

  // Uno stato non raggiungibile non compare in nessun pulsante di avanzamento (lo 0 è quello iniziale)
  const reachable = Number(status.id) === 0
    || (type.statusList || []).some(s => s.id !== status.id && (s.nextStates || []).some(n => Number(n) === Number(status.id)))
  if (!reachable) {
    warnings.push({ blocking: false, text: t('admConfig.statusEditor.warnUnreachable') })
  }

  const expectedKey = `${activeSection.value}.${status.id}`
  if (typeof status.instructionKey === 'string' && status.instructionKey !== expectedKey) {
    warnings.push({ blocking: false, text: t('admConfig.statusEditor.warnKeyNonCanonical', { key: status.instructionKey }) })
  }

  // Riferimento a un'azione che non esiste più nella biblioteca: il pulsante non parte
  const brokenRefs = (status.verifications || [])
    .filter(v => v && v.ref && !aiActions.value.some(a => a.id === v.ref))
    .map(v => v.ref)
  if (brokenRefs.length) {
    warnings.push({ blocking: true, text: t('admConfig.statusEditor.warnMissingAction', { actions: brokenRefs.join(', ') }) })
  }

  // Un'azione spenta non è eseguibile: la sua skill può anche non esistere più
  const missingSkills = (status.verifications || [])
    .filter(v => v && v.enabled !== false && skills.value.length)
    .flatMap(v => {
      const action = aiActions.value.find(a => a.id === v.ref)
      if (!action || action.enabled === false) return []
      const wanted = (action.steps || []).length
        ? action.steps.map(step => step.skill)
        : [action.skill]
      return wanted.filter(skill => skill && !skillExists(skill))
    })
  if (missingSkills.length) {
    warnings.push({ blocking: true, text: t('admConfig.statusEditor.warnMissingSkill', { skills: [...new Set(missingSkills)].join(', ') }) })
  }

  return warnings
}

// Un nextStates che non risolve blocca il salvataggio: il Backend applica la stessa regola
const blockingErrors = computed(() =>
  workingTypes.value.flatMap(type => {
    const ids = new Set((type.statusList || []).map(s => Number(s.id)))
    return (type.statusList || []).flatMap(status =>
      (status.nextStates || [])
        .filter(next => !ids.has(Number(next)))
        .map(next => t('admConfig.statusEditor.blockedDangling', {
          type: type.descr,
          from: `${status.descr} (${status.id})`,
          to: next
        }))
    )
  })
)

const workflowPreview = computed(() => {
  const type = activeType.value
  if (!type) return ''
  return (type.statusList || []).map(status => {
    const next = Array.isArray(status.nextStates) && status.nextStates.length
      ? status.nextStates.join(', ')
      : '—'
    const flags = []
    if (status.isOptional) flags.push('opzionale')
    if (status.exclusive) flags.push('esclusivo')
    if (status.conditionalOn) flags.push(describeConditional(status.conditionalOn))
    const suffix = flags.length ? `   [${flags.join(' · ')}]` : ''
    return `${String(status.id).padStart(3, ' ')} ${status.icon || ' '} ${status.descr}  →  ${next}${suffix}`
  }).join('\n')
})

// ---------- Gruppi di campi ----------

// groupOrder è per tipo di processo: si propongono solo i gruppi dei campi di quel tipo
const knownGroupNames = computed(() => {
  const names = new Set()
  fieldsForActiveType.value.forEach(f => {
    if (f.group?.description) names.add(f.group.description)
  })
  return Array.from(names).sort()
})

const addGroup = () => {
  const name = (newGroupName.value || '').trim()
  if (!name || !activeType.value) return
  if (!Array.isArray(activeType.value.groupOrder)) activeType.value.groupOrder = []
  if (!activeType.value.groupOrder.includes(name)) activeType.value.groupOrder.push(name)
  newGroupName.value = ''
}

const removeGroup = (index) => {
  activeType.value?.groupOrder?.splice(index, 1)
}

const onGroupDrop = (targetIndex) => {
  const from = groupDragIndex.value
  groupDragIndex.value = null
  if (from === null || from === targetIndex || !activeType.value?.groupOrder) return
  const arr = activeType.value.groupOrder.slice()
  const target = arr[targetIndex]
  const [moved] = arr.splice(from, 1)
  const newTargetIndex = arr.indexOf(target)
  arr.splice(from < targetIndex ? newTargetIndex + 1 : newTargetIndex, 0, moved)
  activeType.value.groupOrder = arr
}

// ---------- Riordino stati ----------

const onDrop = (targetIndex) => {
  const from = dragIndex.value
  dragIndex.value = null
  if (from === null || from === targetIndex || !activeType.value) return

  const arr = activeType.value.statusList.slice()
  const target = arr[targetIndex]
  const [moved] = arr.splice(from, 1)
  const newTargetIndex = arr.indexOf(target)
  // Trascinando verso il basso lo stato si posiziona dopo il target, verso l'alto prima
  arr.splice(from < targetIndex ? newTargetIndex + 1 : newTargetIndex, 0, moved)
  activeType.value.statusList = arr
}

// ---------- Modifica di uno stato ----------

const editDialog = ref(false)
const editingIndex = ref(null)
const editingStatus = ref(null)
const editingInstructionsHtml = ref('')

const editStatus = (index) => {
  const status = activeType.value.statusList[index]
  editingIndex.value = index
  editingStatus.value = JSON.parse(JSON.stringify(status))
  editingInstructionsHtml.value = instructionsHtmlFor(status)
  editDialog.value = true
}

const addStatus = () => {
  if (!activeType.value) return
  const used = new Set((activeType.value.statusList || []).map(s => Number(s.id)))
  let nextId = 1
  while (used.has(nextId)) nextId++

  editingIndex.value = null
  editingStatus.value = {
    descr: '',
    id: nextId,
    color: '#9E9E9E',
    icon: '•',
    group: activeType.value.statusList?.[0]?.group,
    nextStates: [],
    instructionKey: `${activeSection.value}.${nextId}`
  }
  // Chiave orfana già presente: si riparte da quel contenuto invece di mostrarlo vuoto
  editingInstructionsHtml.value = workingInstructions.value[activeSection.value]?.[String(nextId)] || ''
  editDialog.value = true
}

const setInstructionsHtml = (key, html) => {
  if (typeof key !== 'string' || !key.includes('.')) return
  const [section, statusKey] = key.split('.')
  const text = (html || '').trim()

  if (!text) {
    if (workingInstructions.value[section]) delete workingInstructions.value[section][statusKey]
    return
  }
  // La sezione mancante (per esempio 'outros') viene creata solo quando c'è davvero contenuto
  if (!workingInstructions.value[section]) workingInstructions.value[section] = {}
  workingInstructions.value[section][statusKey] = html
}

const onStatusCommit = ({ status, instructionsHtml, previousKey }) => {
  const canonicalStatus = canonical(status, STATUS_PROPS)

  if (editingIndex.value === null) {
    activeType.value.statusList.push(canonicalStatus)
  } else {
    activeType.value.statusList.splice(editingIndex.value, 1, canonicalStatus)
  }

  // Chiave rinominata dall'utente: si sposta il contenuto invece di lasciarlo sulla vecchia
  if (previousKey && previousKey !== canonicalStatus.instructionKey) {
    setInstructionsHtml(previousKey, '')
  }
  setInstructionsHtml(canonicalStatus.instructionKey, instructionsHtml)

  editDialog.value = false
}

// ---------- Analisi impatto ----------

const impactDialog = ref(false)
const impactOperation = ref('delete')
const impactStatus = ref(null)

const startImpact = (operation, status) => {
  impactOperation.value = operation
  impactStatus.value = JSON.parse(JSON.stringify(status))
  impactDialog.value = true
}

const rekeyFields = (typeId, oldId, newId) => {
  // Clone dei soli campi di questo tipo: viene salvato con il PUT già esistente, se l'utente lo chiede
  const draft = JSON.parse(JSON.stringify(fieldsByType.value[String(typeId)] || []))
  const oldKey = String(oldId)
  draft.forEach(field => {
    if (field.validationByStatus && field.validationByStatus[oldKey] !== undefined) {
      if (newId === null) {
        delete field.validationByStatus[oldKey]
      } else {
        field.validationByStatus[String(newId)] = field.validationByStatus[oldKey]
        delete field.validationByStatus[oldKey]
      }
    }
    if (Array.isArray(field.editableInStatuses)) {
      field.editableInStatuses = newId === null
        ? field.editableInStatuses.filter(v => String(v) !== oldKey)
        : field.editableInStatuses.map(v => (String(v) === oldKey ? newId : v))
    }
  })
  pendingFieldsSave.value = { typeId, fields: draft }
}

const onImpactConfirm = ({ operation, statusId, newId, options }) => {
  const type = activeType.value
  if (!type) return
  const list = type.statusList
  const index = list.findIndex(s => Number(s.id) === Number(statusId))

  if (operation === 'delete') {
    if (index === -1) return
    const removed = list[index]
    list.splice(index, 1)

    if (options.updateNextStates) {
      list.forEach(s => {
        if (Array.isArray(s.nextStates)) {
          s.nextStates = s.nextStates.filter(n => Number(n) !== Number(statusId))
        }
      })
    }
    if (options.updateInstructions) setInstructionsHtml(removed.instructionKey, '')
    if (options.updateFields) rekeyFields(type.id, statusId, null)
  }

  if (operation === 'changeId') {
    if (index === -1) return
    const status = list[index]
    const previousKey = status.instructionKey

    if (options.updateNextStates) {
      list.forEach(s => {
        if (Array.isArray(s.nextStates)) {
          s.nextStates = s.nextStates.map(n => (Number(n) === Number(statusId) ? Number(newId) : Number(n)))
        }
      })
    }
    if (options.updateInstructions && typeof previousKey === 'string') {
      const html = instructionsHtmlFor(status)
      const [section] = previousKey.split('.')
      status.instructionKey = `${section}.${newId}`
      setInstructionsHtml(previousKey, '')
      setInstructionsHtml(status.instructionKey, html)
    }
    if (options.updateFields) rekeyFields(type.id, statusId, Number(newId))

    status.id = Number(newId)
  }

  if (operation === 'duplicate') {
    if (index === -1) return
    const clone = canonical(JSON.parse(JSON.stringify(list[index])), STATUS_PROPS)
    clone.id = Number(newId)
    clone.descr = `${clone.descr} (copia)`
    if (typeof clone.instructionKey === 'string') {
      const [section] = clone.instructionKey.split('.')
      clone.instructionKey = `${section}.${newId}`
    }
    if (options.copyInstructions) {
      setInstructionsHtml(clone.instructionKey, instructionsHtmlFor(list[index]))
    }
    list.splice(index + 1, 0, clone)
  }

  impactDialog.value = false
}

// ---------- Apertura / chiusura ----------

const editorDialog = ref(false)
const confirmCloseDialog = ref(false)

const openEditor = async () => {
  // Riallinea alla configurazione sul server, ma senza buttare via un lavoro in corso
  if (!hasChanges.value) {
    await projectStore.loadConfigurations()
    resetChanges()
  }
  if (workingTypes.value.length && !workingTypes.value.some(type => type.id === activeTypeId.value)) {
    activeTypeId.value = workingTypes.value[0].id
  }
  // Conteggi e skill invecchiano in fretta: si rileggono a ogni apertura
  try {
    skills.value = await projectStore.loadSkills()
    statusCounts.value = await projectStore.loadStatusCounts()
  } catch (e) {
    console.error('Errore nel caricamento di skill e conteggi:', e)
  }
  editorDialog.value = true
}

const requestClose = () => {
  if (hasChanges.value) {
    confirmCloseDialog.value = true
    return
  }
  editorDialog.value = false
}

const discardAndClose = () => {
  resetChanges()
  confirmCloseDialog.value = false
  editorDialog.value = false
}

const saveAndClose = async () => {
  await save()
  // Con errore di salvataggio si resta nell'editor: chiudere perderebbe le modifiche
  if (!saveError.value) {
    confirmCloseDialog.value = false
    editorDialog.value = false
  } else {
    confirmCloseDialog.value = false
  }
}

onMounted(async () => {
  if (!projectStore.configurations?.config) {
    await projectStore.loadConfigurations()
  }
  loadFromStore()
  if (workingTypes.value.length) activeTypeId.value = workingTypes.value[0].id
})

// ---------- Salvataggio ----------

const save = async () => {
  if (blockingErrors.value.length) return

  saving.value = true
  saveError.value = ''
  saveErrorDetails.value = []
  saveSuccess.value = ''
  try {
    // Campi prima: la riscrittura delle chiavi è opzionale e indipendente dal resto.
    // La mappa si ricostruisce completa al salvataggio: il Backend rifiuta un payload parziale.
    if (pendingFieldsSave.value) {
      const byType = JSON.parse(JSON.stringify(projectStore.fieldsByProjectType || {}))
      byType[String(pendingFieldsSave.value.typeId)] = pendingFieldsSave.value.fields
      await projectStore.saveFieldsByType(byType)
      pendingFieldsSave.value = null
    }
    // Istruzioni prima di cfg: se il secondo PUT fallisce restano al più chiavi orfane, che sono
    // inerti. L'ordine inverso lascerebbe uno stato che punta a una chiave inesistente.
    let backup = ''
    if (hasInstructionsChanges.value) {
      const result = await projectStore.saveInstructions(workingInstructions.value)
      backup = result?.backup || ''
    }
    let count = 0
    if (hasCfgChanges.value) {
      const result = await projectStore.saveCfgParts({ projectTypes: workingTypes.value })
      count = result?.count ?? 0
      backup = result?.backup || backup
    }
    loadFromStore()
    saveSuccess.value = t('admConfig.statusEditor.saved', { count, backup: backup || '—' })
  } catch (e) {
    saveError.value = e?.message || t('admConfig.statusEditor.saveError')
    const details = e?.response?.data?.errors
    if (Array.isArray(details)) saveErrorDetails.value = details
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.status-row {
  border: 1px solid rgba(0, 0, 0, 0.08);
  margin-bottom: 4px;
  cursor: grab;
  background: rgba(255, 255, 255, 0.6);
}
.status-row:hover {
  background: rgba(0, 0, 0, 0.03);
}
.status-row--dragging {
  opacity: 0.4;
}
.drag-handle {
  cursor: grab;
}
.status-swatch {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  display: inline-block;
  border: 1px solid rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
}
.status-icon {
  font-size: 1.1rem;
  line-height: 1;
}
.group-chip--dragging {
  opacity: 0.4;
}
.json-preview {
  max-height: 420px;
  overflow: auto;
  font-size: 0.75rem;
  background: rgba(0, 0, 0, 0.03);
  padding: 8px;
  border-radius: 4px;
}
</style>
